import { Fragment } from 'react'

import { AppRoutes } from '@constants/app-routes'
import { theme } from '@theme'
import { PostConfig } from '@ui/article/ArticleGenerator/types'
import { OrangeLink } from '@ui/links/OrangeLink'

import { AuthorBlock } from './AuthorBlock'
import { BenefitsBlock } from './BenefitsBlock'
import {
  FormTitle,
  HeaderTitleWrapper,
  OrangeSpan,
  TextTitle,
  TitleWrapper,
  ListSpan,
} from './styles'
import { TrendsBlock } from './TrendsBlock'
import { ListSpan as TrendsListSpan } from './TrendsBlock/styles'

const baseURL = '/images/articles/medtech-2024-trends-insights'

export const mobileHeaderProps = {
  imagePath: `${baseURL}/bg-mobile.webp`,
  bottom: '18%',
}

export const mobileEmailFormProps = {
  title: (
    <FormTitle>
      Stay Tuned{'\n'}You have free access to articles, guides, useful tips, and
      industry insights from our experts
    </FormTitle>
  ),
}

const trendsList = [
  {
    id: 1,
    text: (
      <>
        <TrendsListSpan>Ageing population:</TrendsListSpan> According to current
        forecasts, the EU’s population is expected to peak at{' '}
        <OrangeLink
          inherit
          target="_blank"
          href="https://www.gisreportsonline.com/r/europe-population-decline/"
        >
          526 million
        </OrangeLink>{' '}
        by the 2040s, necessitating specialized healthcare technology.
      </>
    ),
  },
  {
    id: 2,
    text: (
      <>
        <TrendsListSpan>Market size:</TrendsListSpan> The global medtech market
        is{' '}
        <OrangeLink
          inherit
          target="_blank"
          href="https://www.statista.com/outlook/hmo/medical-technology/worldwide"
        >
          projected
        </OrangeLink>{' '}
        to grow by 4.73%{'\n'}(2023-2028) resulting in a market volume of
        US$719.20bn in 2028.
      </>
    ),
  },
  {
    id: 3,
    text: (
      <>
        <TrendsListSpan>Demand for sustainability:</TrendsListSpan> Younger
        demographics are encouraging medtech manufacturers to prioritize
        sustainability in their agendas
      </>
    ),
  },
]

const benefitsList = [
  {
    id: 1,
    title: '500+',
    text: 'collective hours by seasoned medtech consultants',
  },
  {
    id: 2,
    title: '7',
    text: 'non-profit incubators globally',
  },
  {
    id: 3,
    title: '28',
    text: 'startups and emerging medtech firms',
  },
  {
    id: 4,
    title: 'Top topics',
    text: 'egulatory compliance, software strategies, cybersecurity and market entry',
  },
]

const recommendationsList = [
  <Fragment key={1}>
    <ListSpan>Don’t neglect cybersecurity: </ListSpan>
    Establishing robust security measures to safeguard sensitive medical data
    and mitigate the risks associated with cyber threats is a non-negotiable
    aspect of medtech operations. Ensuring data integrity and patient
    confidentiality is pivotal in the digital age.
  </Fragment>,
  <Fragment key={2}>
    <ListSpan>Stay informed on regulatory changes:</ListSpan> Keeping abreast of
    evolving regulatory standards, especially concerning Software as a Medical
    Device, is critical. Adapting swiftly to these changes ensures compliance
    and upholds the highest standards of quality and patient safety within the
    medtech industry.
  </Fragment>,
  <Fragment key={3}>
    <ListSpan>Rethink silos and embrace the cloud: </ListSpan>
    Breaking down silos within product portfolios by adopting integrated
    ecosystems, fortified with scalable technology stacks, is paramount. This
    approach fosters seamless communication and data sharing, enabling more
    efficient operations across the entire medtech spectrum.
  </Fragment>,
  <Fragment key={4}>
    <ListSpan>
      Rather than focusing solely on devices, think about the system:{' '}
    </ListSpan>
    Prioritizing digital innovation and fostering a network of connected devices
    that work collectively to elevate patient care is essential. By focusing on
    holistic systems rather than individual devices, medtech companies can
    significantly enhance the quality and effectiveness of healthcare delivery.
  </Fragment>,
  <Fragment key={5}>
    <ListSpan>Pursue global market expansion with local adaptation: </ListSpan>
    Tailor your products and services to meet the specific cultural, linguistic,
    and regulatory nuances of different markets. Establishing strong local
    partnerships and understanding regional needs are critical for successful
    global penetration.
  </Fragment>,
]

export const config: PostConfig = {
  pageUniqId: 'Medtech2024TrendsInsights',
  headerProps: {
    imagePath: `${baseURL}/bg.webp`,
    imageTitle: 'Medtech 2024 trends',
    imageAlt: 'Medtech 2024 trends',
    title: (
      <HeaderTitleWrapper>
        <OrangeSpan>Medtech 2024:</OrangeSpan> Trends, insights, and our
        impactful advisory initiative
      </HeaderTitleWrapper>
    ),
    titleWidth: '850px',
    titleTransform: '34%',
    position: '95%',
    bottom: '16%',
    hexagonLeft: false,
    hexagonRight: false,
    hexagonLeftPosition: '0%',
    hexagonRightPosition: '0%',
    overlay: true,
  },
  emailFormProps: {
    title: (
      <FormTitle>
        Stay Tuned{'\n'}You have free access to articles, guides, useful tips,
        and industry{'\n'}insights from our experts
      </FormTitle>
    ),
    titleSubmited: '\nThanks, we’ve got it!',
    imageDetail: {
      path: '/images/email-subscription-blog.svg',
      width: 232,
      height: 230,
    },
    imageDetailSubmited: {
      path: '/images/stay-tuned-submited.webp',
      width: 232,
      height: 193,
    },
    marginTop: '0',
  },
  article: [
    {
      type: 'custom',
      value: (
        <AuthorBlock
          image={{
            src: `${baseURL}/ekaterina-zharikova.webp`,
            alt: 'medtech software development expert',
            title: 'medtech software development expert',
            width: 100,
            height: 100,
            quality: 100,
          }}
          title="Ekaterina Zharikova"
          description="Medtech Software Development Expert"
        />
      ),
      marginTop: `${theme.newSpaces.xl5}px`,
    },
    {
      type: 'paragraph',
      value: (
        <>
          Across the medtech industry, where technology and healthcare
          intersect, the upcoming journey into 2024 promises a landscape rich
          with innovation, challenges, and opportunities. This year has been
          pivotal for the Modsen team—a time of significant strides in
          technology and a commitment to meaningful initiatives. At the core of
          our mission is the belief that technology should serve humanity and
          support healthcare professionals. As we delve into the future, we’ll
          explore emerging trends and insights gathered from our experience as
          experts in{' '}
          <OrangeLink inherit href={AppRoutes.healthcare} target="_blank">
            medtech software development.
          </OrangeLink>{' '}
          From breakthroughs to regulatory concerns and cybersecurity, our goal
          is to navigate the changes, providing solutions and expertise to top
          companies in the industry while leveraging AI and other pivotal
          advancements for a better tomorrow.
        </>
      ),
      marginTop: `${theme.newSpaces.lg}px`,
    },
    {
      type: 'image',
      value: {
        src: `${baseURL}/pic-1.webp`,
        title: 'healthcare software trends',
        alt: 'healthcare software trends',
        width: 2620,
        height: 1552,
        quality: 100,
      },
      marginTop: `${theme.newSpaces.lg}px`,
    },
    {
      type: 'subtitle',
      value: (
        <TitleWrapper>
          Modsen counselling project 2023: Supporting non-profit medtech
          incubators and accelerators
        </TitleWrapper>
      ),
      marginTop: '0',
      subtitleLevel: 'h2',
    },
    {
      type: 'paragraph',
      value:
        'The year 2023 marked a significant stride for Modsen, extending our impact beyond technological innovation into a realm of profound industry involvement. We launched a unique initiative: offering pro bono counselling services tailored specifically for non-profit medical technology incubators and accelerators.',
      marginTop: `${theme.newSpaces.lg}px`,
    },
    {
      type: 'custom',
      value: (
        <BenefitsBlock
          title="Modsen medtech incubator counseling project 2023"
          items={benefitsList}
        />
      ),
    },
    {
      type: 'paragraph',
      value:
        'The project wasn’t merely an altruistic endeavour but a strategic step, solidifying our commitment to understanding and actively shaping the future trends in the medtech sector. By implementing the program, we aimed to provide specialized guidance, leveraging our expertise to support emerging ventures dedicated to healthcare technology advancement.',
      marginTop: `${theme.newSpaces.lg}px`,
    },
    {
      type: 'paragraph',
      value:
        'Throughout the year, our team of seasoned consultants dedicated over 500 hours collectively to mentorship sessions, workshops, and tailored advisory meetings. A wide array of topics were specifically designed to address critical challenges faced by emerging medtech entities, including regulatory compliance, software development strategies, cybersecurity best practices, and market readiness assessments.',
    },
    {
      type: 'paragraph',
      value:
        'The impact of the initiative was profound. We directly engaged with 7 non-profit incubators and accelerators, spanning across different regions, providing tailored guidance to 28 startups and emerging medtech companies. Our consultations facilitated the refinement of business strategies, enhancing technological capabilities, and guiding these ventures toward impactful market entry.',
    },
    {
      type: 'paragraph',
      value:
        'Beyond the numbers, this initiative served as a conduit for us to intimately understand the evolving needs and challenges faced by budding medtech innovators. It provided us with firsthand insights into the pulse of the industry, shaping our perspectives and approaches toward future technological developments.',
    },
    {
      type: 'paragraph',
      value:
        'Excitedly, we are eager to share these valuable insights, although we can’t divulge all the details due to our commitment to respecting business confidentiality.',
    },
    {
      type: 'subtitle',
      value: (
        <TitleWrapper>
          Applying 5 medtech trends in action for 2024
        </TitleWrapper>
      ),
      marginTop: '0',
      subtitleLevel: 'h2',
    },
    {
      type: 'image',
      value: {
        src: `${baseURL}/pic-2.webp`,
        title: 'medtech trends',
        alt: 'medtech trends',
        width: 2620,
        height: 1552,
        quality: 100,
      },
      marginTop: `${theme.newSpaces.lg}px`,
    },
    {
      type: 'custom',
      value: <TrendsBlock title="At a glance:" list={trendsList} />,
    },
    {
      type: 'subtitle',
      value: (
        <TextTitle>
          Trend 1. AI and large language models (LLMs) reshaping healthcare
          practices
        </TextTitle>
      ),
      marginTop: `${theme.newSpaces.lg}px`,
      subtitleLevel: 'h3',
    },
    {
      type: 'paragraph',
      value: (
        <>
          <OrangeLink
            inherit
            href={AppRoutes.aiPoweredPlatform}
            target="_blank"
          >
            The profound impact of AI
          </OrangeLink>{' '}
          and LLMs on reshaping healthcare practices is undeniable. These
          innovations aren’t just theoretical concepts; they’re actively
          revolutionizing the field with tangible applications and future
          potential.
        </>
      ),
    },
    {
      type: 'paragraph',
      value:
        'Specifically, the incorporation of AI into surgical procedures represents a significant leap. This integration holds the promise of providing surgeons with real-time audio support during operations, thereby improving precision and efficiency. Moreover, it has the potential to streamline personalized post-operative care through automated transcripts, facilitating more precise and comprehensive patient records.',
    },
    {
      type: 'subtitle',
      value: (
        <TextTitle>
          Trend 2. Medtech’s eco-initiative: focusing on sustainability
        </TextTitle>
      ),
      marginTop: `${theme.newSpaces.sm}px`,
      subtitleLevel: 'h3',
    },
    {
      type: 'paragraph',
      value:
        'The increasing emphasis on eco-friendly practices within the medtech industry mirrors a growing environmental consciousness, particularly among younger demographics. Companies are exploring the use of sustainable materials sourced from plants and sugars to create recyclable medical devices, aligning with preservation goals.',
    },
    {
      type: 'paragraph',
      value: (
        <>
          Furthermore, this eco-conscious drive extends beyond product
          development, encompassing software and hardware endeavours. Efforts
          are directed toward{' '}
          <OrangeLink
            inherit
            href={AppRoutes.sustainableSoftwareDevelopment}
            target="_blank"
          >
            adopting energy-efficient practices,
          </OrangeLink>{' '}
          optimizing resource consumption during manufacturing, and implementing
          eco-friendly software architecture.
        </>
      ),
    },
    {
      type: 'subtitle',
      value: (
        <TextTitle>
          Trend 3. The demand for transparent ROI in medtech
        </TextTitle>
      ),
      marginTop: `${theme.newSpaces.sm}px`,
      subtitleLevel: 'h3',
    },
    {
      type: 'paragraph',
      value: (
        <>
          In response to the financial instabilities observed in 2023, medtech
          companies face heightened pressure to{' '}
          <OrangeLink inherit href={AppRoutes.telemedicine} target="_blank">
            showcase clear returns on investment,
          </OrangeLink>{' '}
          particularly concerning data and software acquisitions. This
          intensified demand for transparent ROI necessitates dynamic solutions
          that seamlessly integrate data, software, and analytics. Companies
          actively seek comprehensive strategies capable of triggering real-time
          actions while precisely measuring and tracking the return on
          investment.
        </>
      ),
    },
    {
      type: 'paragraph',
      value:
        'The focus on ROI effectiveness drives medtech companies toward solutions that not only encompass diverse data sets and software capabilities but also prioritize analytics-driven decision-making. The objective is to develop agile systems enabling prompt actions, leveraging real-time insights from data acquisitions and software implementations.',
    },
    {
      type: 'subtitle',
      value: <TextTitle>Trend 4. Medtech’s IDN synergy</TextTitle>,
      marginTop: `${theme.newSpaces.sm}px`,
      subtitleLevel: 'h3',
    },
    {
      type: 'paragraph',
      value:
        'With integrated delivery networks (IDNs) wielding purchasing power, medtech companies are increasingly forging deeper collaborations. These alliances are not merely about tapping into a larger market but entail establishing strategic partnerships to provide comprehensive solutions across various medical specialities. The focus isn’t solely on individual product sales but on creating innovative contract structures that offer bundled solutions catering to the diverse needs of these networks.',
    },
    {
      type: 'paragraph',
      value: (
        <>
          The success of such collaborations hinges on the{' '}
          <OrangeLink
            inherit
            href={AppRoutes.applicationForMedicalEquipmentProvision}
            target="_blank"
          >
            integration of robust data insights.
          </OrangeLink>{' '}
          Medtech companies aiming to thrive within IDNs recognize the paramount
          importance of leveraging data analytics to understand the intricate
          needs and preferences of these networks. As a result, they can tailor
          their offerings, optimizing their solutions to fit seamlessly within
          the network’s operations and enhancing the value proposition beyond
          mere product sales.
        </>
      ),
    },
    {
      type: 'subtitle',
      value: (
        <TextTitle>
          Trend 5. The power of data: diversity in medtech’s workforce
        </TextTitle>
      ),
      marginTop: `${theme.newSpaces.sm}px`,
      subtitleLevel: 'h3',
    },
    {
      type: 'paragraph',
      value: (
        <>
          Granting in-house teams immediate{' '}
          <OrangeLink
            inherit
            href={AppRoutes.bigDataMedicalRecordSystemLayout}
            target="_blank"
          >
            access to real-time data
          </OrangeLink>{' '}
          is a strategic move that goes beyond enhancing performance; it
          catalyzes diversifying the talent pool within medtech firms. The
          democratization of data access creates an environment where a more
          diverse range of professionals, both seasoned and new, can thrive. By
          breaking down barriers to insights, medtech companies attract
          individuals with varied backgrounds and perspectives, fostering a more
          agile and adaptable workforce. This amalgamation of experience and
          fresh viewpoints also contributes to a more dynamic and innovative
          sales team, equipped to navigate the evolving landscape of the
          industry.
        </>
      ),
    },
    {
      type: 'subtitle',
      value: (
        <TitleWrapper>
          Guiding principles: Modsen’s five medtech recommendations
        </TitleWrapper>
      ),
      marginTop: '0',
      subtitleLevel: 'h2',
    },
    {
      type: 'image',
      value: {
        src: `${baseURL}/pic-3.webp`,
        title: "Modsen's software recommendations",
        alt: "Modsen's software recommendations",
        width: 2620,
        height: 1968,
        quality: 100,
      },
      marginTop: `${theme.newSpaces.lg}px`,
    },
    {
      type: 'paragraph',
      value:
        'As the medtech landscape undergoes significant shifts driven by technological advancements and evolving trends, it becomes imperative for companies to navigate these changes strategically. Here are actionable insights gleaned from our experience, intended to guide medtech firms through this transformative phase:',
      marginTop: `${theme.newSpaces.lg}px`,
    },
    {
      type: 'list',
      value: recommendationsList,
      listType: 'ol',
      marginTop: `${theme.newSpaces.sm}px`,
      paddingLeft: `${theme.newSpaces.sm}px`,
      gap: '0',
      gapMobile: `${theme.newSpaces.xs}px`,
      textAlign: 'justify',
    },
    {
      type: 'paragraph',
      value:
        'By integrating these strategic pillars into their operational framework, medtech companies can navigate the dynamic landscape with resilience and innovation, thereby fostering advancements that positively impact the healthcare sector.',
    },
    {
      type: 'subtitle',
      value: <TitleWrapper>Instead of conclusion</TitleWrapper>,
      marginTop: '0',
      subtitleLevel: 'h2',
    },
    {
      type: 'paragraph',
      value:
        'Embrace these insights to drive impactful change within the medtech sphere. The future of healthcare technology is calling for innovation. Will you be the architect of transformation?',
      marginTop: `${theme.newSpaces.lg}px`,
    },
    {
      type: 'paragraph',
      value: (
        <>
          Moreover, in our pursuit of driving progress,{' '}
          <OrangeLink inherit href={AppRoutes.letsWork} target="_blank">
            we’re excited to continue our advisory initiative in 2024.
          </OrangeLink>{' '}
          We welcome new partnerships in this endeavour, fostering collaborative
          efforts to shape the future of medtech.
        </>
      ),
    },
  ],
}
